import { useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DocumentEditRules } from '../../../../../../features';
import { convertFieldKeyToFieldInfo } from '../../../../../../features/DocumentEditRules/PluginHost/services/associatedFieldService';
import { createFetchCompanyCustomDataArgs } from '../fetchData';
import DropdownOptionsRepository from '../../../../../../features/DocumentEditRules/PluginHost/services/dropdownOptionsRepository';

function convertDropdownData(options: DocumentEditRules.FetchedDropdownOptions) {
    return options.reduce<DocumentEditRules.IFetchedDropdownOption[]>((acc, option) => {
        if (typeof option === 'string') {
            acc.push({ label: option, value: option });
        } else if (option !== null) {
            acc.push(option);
        }
        return acc;
    }, []);
}

function usePrevious<T>(value: T) {
    const ref = useRef<T | undefined>(undefined);

    useEffect(() => {
        ref.current = value;
    }, [value]);

    return ref.current;
}

function getFieldFetcherKey(fetcher: DocumentEditRules.IDropdownOptionsFetcherRegistration, fieldKey: string) {
    if (fetcher.fetcher.doNotDependOnFieldInstance) {
        return fetcher.fetcher.fieldDefinitionKey;
    }

    return `${fetcher.fetcher.fieldDefinitionKey}-${fieldKey}`;
}

function fetcherShouldUpdate(
    fetcher: DocumentEditRules.IDropdownOptionsFetcherRegistration,
    oldContext: DocumentEditRules.DocumentEditValidationState,
    newContext: DocumentEditRules.DocumentEditValidationState,
    datasetName: string,
    datasetPath: string
) {
    try {
        return fetcher.fetcher.shouldUpdate(oldContext, newContext, { datasetName, datasetPath });
    } catch (e) {
        console.error(
            `Pro Serv - An error ocurred in your fetcher's "shouldUpdate" function. Your fetcher will not be called to fetch options.`,
            e,
            fetcher
        );
        return false;
    }
}

const skipFetching = [
    ['UnknownFetcher'],
    () => [],
    {
        enabled: false,
    },
] as const;

function useFetcherQueryArgsV1(
    fetcher: DocumentEditRules.IDropdownOptionsFetcherRegistration | undefined,
    context: DocumentEditRules.DocumentEditValidationState | undefined,
    fieldKey: string,
    datasetName: string,
    datasetPath: string
) {
    const change = useRef(0);
    const oldContext = usePrevious(context);

    return useMemo(() => {
        if (!fetcher) {
            return skipFetching;
        }

        if (!context) {
            return skipFetching;
        }

        if (oldContext && fetcherShouldUpdate(fetcher, oldContext, context, datasetName, datasetPath)) {
            change.current += 1;
        }

        const queryKey = `DropdownOptionsFetcher-${getFieldFetcherKey(fetcher, fieldKey)}-${change.current}`;

        return [
            [queryKey],
            async (): Promise<DocumentEditRules.IFetchedDropdownOption[]> => {
                try {
                    const options = await fetcher.fetcher.fetchOptions(context, convertFieldKeyToFieldInfo(fieldKey), {
                        datasetName,
                        datasetPath,
                    });
                    return convertDropdownData(options);
                } catch (e) {
                    console.error(
                        `Pro Serv - An error ocurred in your fetcher's "fetchOptions" function. An error will be displayed to the user and the field unusable.`,
                        e,
                        fetcher
                    );
                    throw new Error('Failed to fetch options');
                }
            },
            {
                refetchOnWindowFocus: false,
                refetchOnMount: false,
                refetchOnReconnect: false,
                retry: false,
            },
        ] as const;
    }, [fetcher, context, oldContext, datasetName, datasetPath, fieldKey]);
}

const getDropdownOptionsFetcherV2 = (
    dropdownOptionsRepository: DropdownOptionsRepository | undefined,
    fieldDefinitionKey: string | undefined,
    jsEngineVersion: number
) => {
    if (jsEngineVersion !== 2) {
        return undefined;
    }
    return async () => {
        if (dropdownOptionsRepository && fieldDefinitionKey) {
            let dropdownData = await dropdownOptionsRepository.getLatestDropdownOptions(fieldDefinitionKey);
            if (dropdownData && dropdownData.length) {
                dropdownData = convertDropdownData(dropdownData);
                return dropdownData;
            } else {
                return [];
            }
        } else {
            return undefined;
        }
    };
};

export function useDropdownOptions(
    fieldDefinitionKey: string | undefined,
    datasetName: string | undefined,
    datasetPath: string | undefined,
    companyId: number,
    dropdownOptionsRepository: DropdownOptionsRepository,
    jsEngineVersion: number
) {
    const validator = DocumentEditRules.useDocumentEditValidator();
    const fieldName = DocumentEditRules.useContextualFieldName();
    const fieldKey = DocumentEditRules.useFieldKey(fieldName);

    const fetcherV1 =
        fieldDefinitionKey && jsEngineVersion !== 2
            ? validator.getDropdownOptionsFetcher(fieldDefinitionKey)
            : undefined;

    // V1 fetcher when fieldDefinitionKey is present
    const queryArgsV1 = useFetcherQueryArgsV1(
        fetcherV1,
        validator.context,
        fieldKey,
        datasetName ?? '',
        datasetPath ?? ''
    );

    const queryClientV2 = useQueryClient();

    const queryKeyV2 = useMemo(() => {
        return [dropdownOptionsRepository, fieldDefinitionKey];
    }, [dropdownOptionsRepository, fieldDefinitionKey]);

    // Invalidate the query when the dropdownOptionsRepository updates the options
    // dropdownOptionsRepository will call this callback function when options are set with the fieldDefinitionKey
    const invalidateQueryV2 = useCallback(() => {
        queryClientV2.invalidateQueries(queryKeyV2);
    }, [queryKeyV2, queryClientV2]);

    // Register the callback function to be called only once
    useEffect(() => {
        if (fieldDefinitionKey && jsEngineVersion === 2) {
            dropdownOptionsRepository.registerCallback(fieldDefinitionKey, invalidateQueryV2);
        }
    }, [fieldDefinitionKey, jsEngineVersion, dropdownOptionsRepository, invalidateQueryV2]);

    // V2 fetcher when fieldDefinitionKey is present
    const queryArgsV2 = [
        queryKeyV2,
        getDropdownOptionsFetcherV2(dropdownOptionsRepository, fieldDefinitionKey, jsEngineVersion),
        {
            enabled: !!fieldDefinitionKey && jsEngineVersion === 2,
        },
    ];

    // if fieldDefinitionKey is not present, the UI is expected to fetch the company custom datasets and populate the dropdown
    const queryArgsUI = fieldDefinitionKey
        ? undefined
        : createFetchCompanyCustomDataArgs(datasetName, datasetPath, companyId);

    const queryArgs = fieldDefinitionKey ? (jsEngineVersion === 2 ? queryArgsV2 : queryArgsV1) : queryArgsUI;

    return useQuery<DocumentEditRules.IFetchedDropdownOption[]>(...(queryArgs as [any, any, any]));
}
